<template>
  <MediaLibraryDetailLayout
    :media-library="mediaLibrary"
    :loading="mediaLibraryLoading"
  >
    <template #rightHeader>
      <button class="backButton c-button__item c-button__item--outline" @click="onBack">
        {{ $t('labels.backToPreviousPage') }}
      </button>
    </template>

    <template #moreDetail>
      <h3 class="detail__title p-box__title">
        {{ $t('pages.CompaniesMediaLibrary.statusOfUse') }}
      </h3>

      <div class="storeCount">
        {{ $t('pages.CompaniesMediaLibrary.totalStoreCount') }}：
        <UnUsedBadge v-if="totalStoreCount === 0" />
        <a v-else href="#storeTable" class="storeCount__value">{{ $t('labels.simpleStoresCount', { count: totalStoreCount }) }}</a>
      </div>

      <div v-if="hasError" class="storeCount status">
        <span class="status__circle--error" />
        <span class="storeCount__value">{{ $t('labels.errorAtStores', { count: mediaLibrary.storeCount.error }) }}</span>
      </div>
      <Info v-if="showOnlyOwnerAuthority">
        <p>
          {{ $t('pages.CompaniesMediaLibrary.info') }}
        </p>
        <p>
          <span class="deleteImage" @click.prevent="onDeleteMediaLibrary">
            {{ $t('pages.CompaniesMediaLibrary.deleteImage') }}
          </span>
        </p>
      </Info>

      <template v-if="mediaLibrary.product">
        <h3 class="detail__title p-box__title">
          {{ $t('pages.CompaniesMediaLibrary.otherUsage') }}
        </h3>

        <div class="registeredProduct">
          {{ $t('pages.CompaniesMediaLibrary.inUseInProductRegistration') }}
          <router-link :to="{ name: 'CompaniesProduct', params: { company_uuid: companyUuid, id: mediaLibrary.product.id }}"
                       class="registeredProduct__value"
          >
            {{ mediaLibrary.product.title }}
          </router-link>
        </div>
      </template>
    </template>

    <template #extra>
      <!-- 店舗一覧テーブル -->
      <div id="storeTable" class="p-table">
        <Loading v-show="tableLoading" />
        <template v-if="stores && !tableLoading">
          <div class="storesTable__header">
            <div class="storesTable__storeCount">
              {{ $t('pages.CompaniesMediaLibrary.storeCount', { count: storesMeta.totalCount }) }}
            </div>
            <div class="storesTable__storeCount">
              <button
                class="c-button__item c-button__item--primary"
                @click="onBulkEdit"
              >
                {{ $t('pages.CompaniesMediaLibrary.bulkSetButton') }}
              </button>
            </div>
          </div>
          <div class="p-table__content">
            <table>
              <thead>
                <tr>
                  <th><span>{{ $t('labels.store') }}</span></th>
                  <th><span>{{ $t('labels.category') }}</span></th>
                  <th><span>{{ $t('pages.CompaniesMediaLibrary.displayingService') }}</span></th>
                  <th><span>{{ $t('labels.status') }}</span></th>
                  <th><span>{{ $t('labels.operation') }}</span></th>
                </tr>
                <tr class="p-table__content__space"><td colspan="6" /></tr>
              </thead>
              <tbody>
                <template v-if="!noStores">
                  <template v-for="(store, i) in stores" :key="`store_${store.id}`">
                    <tr :class="i === stores.length - 1 ? 'storesTable__lastStore' : undefined">
                      <td :rowspan="store.services.length" class="storesTable__storeName">{{ store.name }}</td>
                      <td :rowspan="store.services.length">{{ categoryName(store.gmbCategory) }}</td>
                      <td>{{ serviceName(store.services[0].name) }}</td>
                      <td class="status">
                        <span :class="`status__circle--${ store.services[0].status }`" />
                        <span>
                          {{ $t('pages.CompaniesMediaLibrary.status.' + store.services[0].status ) }}
                        </span>
                      </td>
                      <td>
                        <div class="storesTable__operation">
                          <span class="storesTable__action" @click="onDeleteMediaLibraryStore(store, store.services[0].name)">
                            <Icon name="iconCross" class="storesTable__action__icon" />
                            {{ $t('pages.CompaniesMediaLibrary.cancel') }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <template v-if="store.services.length > 1">
                      <template v-for="(service, j) in store.services" :key="`status_${j}`">
                        <tr v-if="j > 0" :class="j === service.length - 1 ? 'storesTable__lastStore' : undefined">
                          <td>{{ serviceName(service.name) }}</td>
                          <td class="status">
                            <span :class="`status__circle--${ service.status }`" />
                            <span>
                              {{ $t('pages.CompaniesMediaLibrary.status.' + service.status ) }}
                            </span>
                          </td>
                          <td>
                            <div class="storesTable__operation">
                              <span class="storesTable__action" @click="onDeleteMediaLibraryStore(store, service.name)">
                                <Icon name="iconCross" class="storesTable__action__icon" />
                                {{ $t('pages.CompaniesMediaLibrary.cancel') }}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </template>
                </template>
                <div v-else class="noStores" v-text="$t('messages.noData')" />
              </tbody>
            </table>
          </div>
          <div v-if="!noStores" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="tableLoading || currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="tableLoading || currentPage >= storesMeta.totalPages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>
  </MediaLibraryDetailLayout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { mediaLibraryCategoriesConst } from '../../modules/mediaLibraryCategoriesConst'
import MediaLibraryDetailLayout from '../../components/CompaniesMediaLibraries/MediaLibraryDetailLayout.vue'
import Info from '../../components/Info.vue'
import Icon from '../../components/Icon.vue'
import Loading from '../../components/Loading.vue'
import UnUsedBadge from '../../components/CompaniesMediaLibraries/UnUsedBadge'

export default {
  name: 'CompaniesMediaLibrary',
  components: {
    UnUsedBadge,
    MediaLibraryDetailLayout,
    Info,
    Icon,
    Loading
  },
  data () {
    return {
      currentPage: 1,
    }
  },
  computed: {
    ...mapState({
      mediaLibrary: state => state.companiesMediaLibrary.data,
      stores: state => state.companiesMediaLibraryStores.data,
      storesMeta: state => state.companiesMediaLibraryStores.meta,
      mediaLibraryLoading: state => state.companiesMediaLibrary.loading,
      tableLoading: state => state.companiesMediaLibraryStores.loading || state.companiesStoreMediaLibraryStore.loading,
      displayNumber: state => state.filters.displayNumber,
    }),
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
    }),
    companyUuid() { return this.$route.params.company_uuid },
    mediaLibraryId() { return this.$route.params.id },
    noStores() { return !this.stores.length },
    totalStoreCount() {
      const storeCountValues = this.mediaLibrary.storeCount
      return storeCountValues.complete + storeCountValues.progress + storeCountValues.error
    },
    hasError() {
      const storeCountValues = this.mediaLibrary.storeCount

      return storeCountValues.error > 0
    },
    // paging
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.stores.length - 1
    },
    readMediaLibraryStoresQuery() {
      return { per: this.displayNumber, page: this.currentPage }
    },
  },
  created() {
    this.readMediaLibrary(this.mediaLibraryId)
    this.readMediaLibraryStores(this.readMediaLibraryStoresQuery)
  },
  methods: {
    ...mapActions({
      readMediaLibrary: 'companiesMediaLibrary/readMediaLibrary',
      deleteMediaLibrary: 'companiesMediaLibrary/deleteMediaLibrary',
      readMediaLibraryStores: 'companiesMediaLibraryStores/readMediaLibraryStores',
      deleteMediaLibraryStore: 'companiesStoreMediaLibraryStore/deleteMediaLibraryStore',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    categoryName(gmbCategory) {
      return mediaLibraryCategoriesConst.find(categoryConst => categoryConst.id === gmbCategory)?.name
    },
    serviceName(service) {
      switch (service) {
      case 'google_map':
        return this.$t('labels.sites.google')
      case 'yahoo_map':
        return this.$t('labels.sites.yahooMap')
      default:
        return this.$t(`labels.sites.${service}`)
      }
    },
    onBulkEdit() {
      this.$router.push({ name: 'CompaniesMediaLibrariesBulkSet', params: { company_uuid: this.companyUuid }, query: { mediaLibraryId: this.mediaLibraryId }})
    },
    onBack() {
      this.$router.go(-1)
    },
    async onDeleteMediaLibrary() {
      if (!confirm(this.$t('pages.CompaniesMediaLibrary.confirmToDeleteMediaLibrary'))) return

      const payload = {
        media_library_ids: [this.mediaLibraryId]
      }
      const isSuccess = await this.deleteMediaLibrary(payload)
      if (!isSuccess) return

      this.$router.push({ name: 'CompaniesMediaLibraries', params: { company_uuid: this.companyUuid }})
    },
    async onDeleteMediaLibraryStore(store, service) {
      const serviceName = this.serviceName(service)
      if (!confirm(this.$t('pages.CompaniesMediaLibrary.confirmToDeleteMediaLibraryStore', { storeName: store.name, serviceName }))) return

      const isSuccess = await this.deleteMediaLibraryStore({ storeUuid: store.uuid, mediaLibraryId: this.mediaLibraryId, service })
      if (!isSuccess) return

      this.readMediaLibrary(this.mediaLibraryId)
      this.readMediaLibraryStores(this.readMediaLibraryStoresQuery)
    },
    /**
     * 表示件数の変更
     * @param  { Object } e イベントオブジェクト
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.resetPage()
      this.setDisplayNumber(e.target.value)
      this.changePage()
    },
    goPrevPage() {
      --this.currentPage
      this.changePage()
    },
    goNextPage() {
      ++this.currentPage
      this.changePage()
    },
    changePage() {
      this.readMediaLibraryStores(this.readMediaLibraryStoresQuery)
    },
    resetPage() {
      this.currentPage = 1
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/_var.scss";
.backButton {
  font-weight: normal;
  text-decoration: none;
}
.storesTable {
  &__header {
    display: flex;
    justify-content: space-between;
  }
  &__storeCount {
    display: inline-flex;
    align-items: flex-end;
    margin-bottom: 16px;
    font-weight: 700;
  }
  &__storeName {
    background-color: $color-base-i;
  }
  &__operation {
    display: flex;
  }
  &__action {
    display: flex;
    cursor: pointer;
    &__icon {
      margin: 4px 8px 0 0;
    }
  }
  &__lastStore {
    td:first-child,
    td:nth-child(2) {
      border-bottom: 1px solid $color-base-c;
    }
    td:first-child {
      border-bottom-left-radius: 4px;
    }
    &:last-of-type {
      td:first-child:not([rowspan="1"]) {
        border-bottom-left-radius: 0;
      }
    }
    + tr td:first-child {
      border-bottom-left-radius: 0 !important;
    }
  }
}
.noStores {
  margin-top: 16px;
}
.storeCount {
  margin-bottom: 16px;
  &__value {
    color: $color-accent-primary;
    font-weight: bold;
  }
}
.registeredProduct {
  margin-bottom: 16px;
  &__value {
    color: $color-accent-primary;
    font-weight: bold;
  }
}
.deleteImage {
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}
.status {
  %__circle {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 5px;
    border-radius: 100%;
  }
  &__circle {
    &--complete {
      @extend %__circle;
      background: $complete-color;
    }
    &--error {
      @extend %__circle;
      background: $color-accent-primary;
    }
    &--progress {
      @extend %__circle;
      background: #D2D5DB;
    }
  }
  .error {
    color: $color-accent-primary;
    text-decoration: underline;
    cursor: pointer;
  }
}

</style>
